import { Component, inject, Input, OnInit, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import {
  InstitutionTypeaheadComponent,
  InstitutionSelection,
} from "../institution-typeahead/institution-typeahead.component";

import { CheckboxModule } from "@vg-constellation/angular-17/checkbox";
import {
  DropdownMenuData,
  DropdownMenuItem,
  DropdownModule,
} from "@vg-constellation/angular-17/dropdown";
import { HintErrorComponent } from "@vg-constellation/angular-17/hint-error";
import { IconModule } from "@vg-constellation/angular-17/icon";
import { InputComponent, InputMask } from "@vg-constellation/angular-17/input";
import { LabelComponent } from "@vg-constellation/angular-17/label";
import { LinkComponent } from "@vg-constellation/angular-17/link";
import { SwitchModule } from "@vg-constellation/angular-17/switch";

import { IMaskModule } from "angular-imask";

import { AccountOwnerType } from "../../types/AccountOwnerType";
import { WebLogger } from "@weps/shared-http";
import { DropdownTypeaheadDirective } from "@weps/shared-ui";
import { AccountType } from "../../types/AccountType";
import { Account, Asset } from "@models/estateProfile.model";

import * as accountDetailsContent from "@services/content/guided-flow/account-details-content";
import { ACCOUNT_DETAILS_REQUIRED_FIELDS } from "@services/content/guided-flow/account-details-content";
import * as content from "../../services/content/content";

import { GlobalStateStore } from "@services/global-state/global-state.service";
import { AccountOwnerContainerComponent } from "./account-owner-container/account-owner-container.component";
import { BeneficiarySectionComponent } from "../beneficiary-section/beneficiary-section.component";
import { OwnerData } from "./strategies/account-owner-strategy.interface";
import { BeneficiaryState, restoreBeneficiaryState } from "@models/beneficiary.model";
import { mappedAccountTypesMap } from "../../models/account_type_mapping.model";
import { GatekeeperService } from "@services/gatekeeper/gatekeeper.service";
import { FormValidationService } from "../../services/form-validation/form-validation.service";
import { AccountTypeConfigurationService } from "../../services/account-type-configuration/account-type-configuration.service";
import { EditFlowService } from "../../services/edit-flow/edit-flow.service";

@Component({
  selector: "app-account-details",
  standalone: true,
  imports: [
    CommonModule,
    CheckboxModule,
    DropdownModule,
    FormsModule,
    HintErrorComponent,
    IconModule,
    InputComponent,
    IMaskModule,
    LabelComponent,
    LinkComponent,
    SwitchModule,
    AccountOwnerContainerComponent,
    BeneficiarySectionComponent,
    DropdownTypeaheadDirective,
    InstitutionTypeaheadComponent,
  ],
  templateUrl: "./account-details.component.html",
  styleUrls: ["./account-details.component.scss"],
})
export class AccountDetailsComponent implements OnInit {
  readonly globalStore = inject(GlobalStateStore);
  readonly formValidationService = inject(FormValidationService);
  readonly accountTypeConfigService = inject(AccountTypeConfigurationService);
  readonly editFlowService = inject(EditFlowService);
  protected readonly ACCOUNT_DETAILS_SUBACCOUNT_PLACEHOLDER =
    accountDetailsContent.ACCOUNT_DETAILS_SUBACCOUNT_PLACEHOLDER;
  protected readonly ACCOUNT_DETAILS_ACCOUNT_NAME_SUB_TEXT =
    accountDetailsContent.ACCOUNT_DETAILS_ACCOUNT_NAME_SUB_TEXT;
  protected readonly ACCOUNT_DETAILS_INDIVIDUAL_NAME =
    accountDetailsContent.ACCOUNT_DETAILS_INDIVIDUAL_NAME;
  protected readonly ACCOUNT_DETAILS_REQUIRED_FIELDS = ACCOUNT_DETAILS_REQUIRED_FIELDS;

  protected readonly AccountType = AccountType;
  protected readonly AccountOwnerType = AccountOwnerType;

  // WMPS-4245: This accountType should be replaced with inventoryItemType (and the other refs to it in this class)
  @Input() accountType: AccountType;
  @Input() handleBack: any;
  @Input() onHelpClick: (() => void) | null = null;

  protected readonly InputMask = InputMask;
  protected readonly content = content;
  protected readonly accountDetailsContent = accountDetailsContent;

  // Data binding
  accountOwner: string; // POID or UUID
  submitAccountOwner: string; // This is when submitting the data into AGO/MEZ
  accountOwnerUuid: string;
  accountName: string;
  ownerType: AccountOwnerType | string;
  jointType: string | null;
  balance: string | null;
  payableOnDeathDesignation: boolean = false;
  subAccountType: string = ""; // Show's up as Account Type in UI.
  // IMPORTANT: Initialize dropdown data with empty structures to prevent "Cannot read properties of undefined" errors.
  // When trust accounts navigate directly to step 2 (/guided-flow/trust-2), the account-details component
  // renders before ngOnInit completes. Without initialization, the Constellation dropdowns try to access
  // undefined .items and .elRef properties, throwing errors that cause the page to scroll to the bottom.
  // This fix ensures valid data structures exist when the template first renders.
  subAccountTypeValues: DropdownMenuData = { groups: [] };
  individualNameValues: DropdownMenuData = { groups: [] };
  currentSubAccountType: string = "";
  currentAccountOwner: string = "";
  trustDisplayName: string;
  trustLegalName: string;
  trustType: string;

  // Conditional rendering
  showPod: boolean = false;
  showSubAccountType: boolean = false;
  validAccountNameInput: boolean = true;
  validBalanceInput: boolean = true;
  validSubAccountTypeInput: boolean = true;
  validOwnerInput: boolean = true;
  // Trust validation is now handled by the AccountOwnerContainer

  localAccountDetailsContent: any;
  titledUnderLabel: string;
  accountNameLabel: string;
  accountNamePlaceholder: string;
  validMortgageInput: boolean = true;
  mezNameError: string = "";
  mortgageAmount: number | null;
  //TODO: move these to global state.  don't keep local copies of variables.
  isTrustOwnerToggleEnabled: boolean = false;
  isJointOwnerToggleEnabled: boolean = false;
  isBeneficiariesEnabled: boolean = false;

  // Component-based owner selection
  selectedAccountOwnerType: AccountOwnerType = AccountOwnerType.INDIVIDUAL_BY_NAME;
  isEditMode: boolean = false;
  ownerData: OwnerData | null = null;
  fromVgTable: boolean = false;
  ownerCleared: boolean = false;

  // Beneficiary state restored from guided flow (Case A: intra-session back/forward)
  savedBeneficiaryState: BeneficiaryState | null = null;

  // Reference to AccountOwnerContainer for delegation
  @ViewChild(AccountOwnerContainerComponent) ownerContainer: AccountOwnerContainerComponent;
  @ViewChild("beneficiarySection") beneficiarySection: BeneficiarySectionComponent;
  @ViewChild(InstitutionTypeaheadComponent) institutionTypeahead: InstitutionTypeaheadComponent;

  // Institution typeahead state (BANK, INVESTMENT, RETIREMENT_ANNUITY)
  financialInstitutionId: string = "";
  financialInstitutionName: string = "";

  validBeneficiaryInput: boolean = true;

  constructor(protected gatekeeperService: GatekeeperService) {}

  ngOnInit(): void {
    // Check what data we have in global state
    const initState = this.globalStore.getGuidedFlowState(this.accountType);

    // Use existing initState and add onSubmit to it
    this.globalStore.setGuidedFlowInitialState(this.accountType, {
      ...initState,
      onSubmit: this.onSubmit.bind(this),
    });

    this.fromVgTable = this.globalStore.isVanguard(this.accountType);

    // Initialize features from gatekeeper service first
    this.gatekeeperService.getFeatures().subscribe((features) => {
      // Set feature flags
      const trustAsOwnerFeature = features.find((f) => f.featureId === "ENABLE_TRUST_AS_OWNER");
      const jointAsOwnerFeature = features.find((f) => f.featureId === "ENABLE_JOINT_AS_OWNER");
      const beneficiariesFeature = features.find((f) => f.featureId === "ENABLE_BENEFICIARIES");
      this.isTrustOwnerToggleEnabled = trustAsOwnerFeature?.enabled ?? false;
      this.isJointOwnerToggleEnabled = jointAsOwnerFeature?.enabled ?? false;
      this.isBeneficiariesEnabled = beneficiariesFeature?.enabled ?? false;

      // Call setAccountOwner after feature flags are set
      this.setAccountOwner();

      // Filter trust options if trust feature is disabled
      if (!this.isTrustOwnerToggleEnabled) {
        this.subAccountTypeValues = this.filterTrustOptions(this.subAccountTypeValues);
        this.individualNameValues = this.filterTrustOptions(this.individualNameValues);
      }

      // Re-compute conditional rendering now that we have all feature flags
      // This ensures showPod respects both account type config AND beneficiaries toggle
      this.computeConditionalRendering();

      // Log for debugging
      WebLogger.log({
        message: "Gatekeeper features loaded",
        level: "info",
        payload: {
          isBeneficiariesEnabled: this.isBeneficiariesEnabled,
          showPod: this.showPod,
          beneficiariesFeature: beneficiariesFeature,
        },
      });
    });

    // Set up conditional rendering
    this.computeConditionalRendering();

    // Handle edit mode
    // From the accounts-table-v2, if we click into an account, we're also passing in the isEdit property in the stateObj, so we can use that to check if we're in the edit flow or not
    const currentState = this.globalStore.getGuidedFlowState(this.accountType);

    // Restore POD flag and beneficiary state from guided flow state (both edit mode and back-navigation)
    if (currentState.payableOnDeathDesignation) {
      this.payableOnDeathDesignation = true;
      if (currentState.beneficiaryState) {
        // Case A: intra-session back/forward — already have beneficiaries from previous visit
        this.savedBeneficiaryState = currentState.beneficiaryState;
      } else if (currentState.mezBeneficiaries?.length > 0) {
        // Case B: initial edit — restore from MEZ GET response
        this.savedBeneficiaryState = restoreBeneficiaryState(currentState.mezBeneficiaries);
      }
    }

    if (currentState.isEdit) {
      this.isEditMode = true;

      if (this.isAsset(currentState as any)) {
        this.setEditAssetFields();
      } else {
        this.setEditAccountFields();
      }
    }
  }

  filterTrustOptions(dropdownData: DropdownMenuData): DropdownMenuData {
    if (!dropdownData?.groups) return dropdownData;
    return {
      groups: dropdownData.groups.map((group) => ({
        ...group,
        items: group.items.filter((item) => !item?.id?.toString().includes("TRUST")),
      })),
    };
  }

  /**
   * Sets edit fields for accounts using the EditFlowService
   */
  setEditAccountFields(): void {
    this.setEditFields();
  }

  /**
   * Sets edit fields for assets using the EditFlowService
   */
  setEditAssetFields(): void {
    this.setEditFields();
  }

  /**
   * Unified method for setting edit fields for both accounts and assets
   */
  private setEditFields(): void {
    const handlers = {
      ownerSelectorDropdownChangeHandler: this.ownerSelectorDropdownChangeHandler.bind(this),
      nameInputChangeHandler: this.nameInputChangeHandler.bind(this),
      balanceInputChangeHandler: this.balanceInputChangeHandler.bind(this),
      mortgageInputChangeHandler: this.mortgageInputChangeHandler.bind(this),
      subAccountDropdownChangeHandler: this.subAccountDropdownChangeHandler.bind(this),
      institutionChangeHandler: (id: string, name?: string) => {
        this.financialInstitutionId = id;
        this.financialInstitutionName = name || "";
      },
    };

    const context = {
      accountType: this.accountType,
      showSubAccountType: this.showSubAccountType,
    };

    const result = this.editFlowService.setEditFields(context, handlers, this.ownerCleared);

    if (result.currentAccountOwner) {
      this.currentAccountOwner = result.currentAccountOwner;
    }

    if (result.currentSubAccountType) {
      this.currentSubAccountType = result.currentSubAccountType;
    }

    // Set initial balance for edit mode (keep as string for input compatibility)
    const editState = this.globalStore.getGuidedFlowState(this.accountType);
    if (editState && editState.balance) {
      this.balance = editState.balance.toString();
    }
  }

  // eslint-disable-next-line complexity
  setAccountOwner(): void {
    const items: DropdownMenuItem[] = [];
    const currentState = this.globalStore.guidedFlowState();
    const selectedAccountOwnerTypeFromState =
      currentState[this.accountType]["selectedAccountOwnerType"];
    const estateProfile = this.globalStore.estateProfile?.();
    if (!estateProfile) return;

    // Set the owner type for the component-based approach
    const previousOwnerType = this.selectedAccountOwnerType;
    this.selectedAccountOwnerType =
      selectedAccountOwnerTypeFromState || AccountOwnerType.INDIVIDUAL_BY_NAME;
    this.ownerType =
      this.accountTypeConfigService.getAccountOwnerTypeMap()[selectedAccountOwnerTypeFromState];

    if (previousOwnerType && previousOwnerType !== this.selectedAccountOwnerType) {
      this.currentAccountOwner = "";
      this.ownerCleared = true;
    }

    // Declare common variables at the top
    const primaryClient = estateProfile.primaryClient;
    const spouses = this.globalStore.findAllSpouseData();

    // Handle Individual owner type (existing logic for other account types)
    const primaryClientName = primaryClient
      ? primaryClient.firstName + " " + primaryClient.lastName
      : "";

    // Add primary client
    items.push({
      labelText: primaryClientName,
      id: primaryClient.piClientId || primaryClient.uuid,
    });

    // Add all spouses
    for (const spouse of spouses) {
      const spouseName = spouse.firstName + " " + spouse.lastName;
      items.push({
        labelText: spouseName,
        id: spouse.piClientId || spouse.uuid,
      });
    }

    // Add joint ownership options once if any spouse exists
    if (spouses.length > 0 && this.isJointOwnerToggleEnabled) {
      const jointOptions = Object.values(accountDetailsContent.JOINT_OWNERSHIP_TYPES).map(
        (type) => ({
          labelText: type.labelText,
          id: type.id,
        }),
      );
      items.push(...jointOptions);
    }

    /*
     * This is handling the "I'm not sure" account owner selection
     * Don't think we need to set accountOwner but keeping it like that here for now
     * Updating this.ownerType to match the current (3/17/2025) Figma content, upading the ownerType to the display content because of how it was set up on the account-details.component.html inside of the <div class="titled-under"> -> p -> ownerType
     * Updating the submitAccountOwner to be PRIMARY; handling it here instead of inside of the ownerSelectorDropdownChangeHandler function
     */
    // 5/19/25 Temp commenting the I'm Not Sure flow out
    // if (this.ownerType === accountOwnerTypeMap[AccountOwnerType.NOT_SURE]) {
    //   this.accountOwner = accountOwnerTypeMap[AccountOwnerType.NOT_SURE];
    //   this.ownerType = AccountOwnerType.NOT_SURE_DISPLAY;
    //   this.submitAccountOwner = accountDetailsContent.ACCOUNT_OWNER_PRIMARY;
    //   this.accountOwnerUuid = primaryClient["uuid"];
    // }
    this.individualNameValues = this.generateDropdownMenuData(items);
  }

  checkboxChangeHandler(event: any): void {
    const previousState = this.payableOnDeathDesignation;
    this.payableOnDeathDesignation = event["isChecked"]; //isChecked is from the c11n emitted event

    WebLogger.log({
      message: "POD checkbox state changed",
      level: "info",
      payload: {
        event_type: "pod_checkbox_change",
        account_type: this.accountType,
        previous_state: previousState,
        new_state: this.payableOnDeathDesignation,
        beneficiaries_toggle_enabled: this.isBeneficiariesEnabled,
        has_saved_beneficiary_state: !!this.savedBeneficiaryState,
      },
    });

    // Preserve beneficiaryState when unchecking POD so it can be restored on re-check.
    // The beneficiary section component is hidden via *ngIf but savedBeneficiaryState retains the data.
    this.globalStore.updateGuidedFlowTempState(this.accountType, {
      payableOnDeathDesignation: this.payableOnDeathDesignation,
    });
  }

  ownerSelectorDropdownChangeHandler(event: any): void {
    WebLogger.log({ message: "AccountDetails unexpected event", payload: { event } });
  }

  private navigateToAddTrustForm(): void {
    // TODO: Implement trust creation form
  }

  subAccountDropdownChangeHandler(event: any): void {
    // We could map the value back from the enum, but for now we'll just take the string
    this.subAccountType = event["activeItems"][0]; // Since our dropdown is single select, we can just take the first item
    this.validSubAccountTypeInput = this.formValidationService.validateSubAccountTypeInput(
      this.subAccountType,
      this.showSubAccountType,
    );
  }

  validateSubAccountTypeOnBlur(): void {
    setTimeout(() => {
      this.validSubAccountTypeInput = this.formValidationService.validateSubAccountTypeInput(
        this.subAccountType,
        this.showSubAccountType,
      );
    }, 0);
  }

  isInstitutionEligible(): boolean {
    return (
      this.accountType === AccountType.BANK ||
      this.accountType === AccountType.INVESTMENT ||
      this.accountType === AccountType.RETIREMENT_ANNUITY
    );
  }

  institutionSelectedHandler(selection: InstitutionSelection): void {
    this.financialInstitutionId = selection.financialInstitutionId;
    this.financialInstitutionName = selection.financialInstitutionName || "";
  }

  nameInputChangeHandler(input: string): void {
    this.accountName = input;
    this.validAccountNameInput = this.formValidationService.validateAccountNameInput(
      this.accountName,
    );

    if (this.validAccountNameInput) {
      const mezResult = this.isMezCreditorField()
        ? this.formValidationService.validateMezCreditor(this.accountName)
        : this.formValidationService.validateMezAssetLiabilityName(this.accountName);
      this.mezNameError = mezResult.errorMessage;
      if (!mezResult.isValid) {
        this.validAccountNameInput = false;
      }
    } else {
      this.mezNameError = "";
    }
  }

  private isMezCreditorField(): boolean {
    return (
      this.accountType === AccountType.LIABILITIES || this.accountType === AccountType.MORTGAGE
    );
  }

  balanceInputChangeHandler(input: any): void {
    // Regular balance validation for other account types
    this.balance = input === "$" ? null : input;
    this.validBalanceInput = this.formValidationService.validateBalanceInput(this.balance);
  }
  // Handler for mortgage input changes
  mortgageInputChangeHandler(input: any): void {
    this.mortgageAmount = input === "$" ? null : input;
    this.validMortgageInput = this.formValidationService.validateMortgageInput(this.mortgageAmount);
  }

  updateLabels(): void {
    const labels = this.accountTypeConfigService.getAccountTypeLabels(this.accountType);

    this.titledUnderLabel = labels.titledUnderLabel;
    this.accountNameLabel = labels.accountNameLabel;
    this.accountNamePlaceholder = labels.accountNamePlaceholder;
    this.showSubAccountType = labels.showSubAccountType;
  }
  computeConditionalRendering(): void {
    const config = this.accountTypeConfigService.getAccountTypeConfiguration(
      this.accountType,
      this.ownerType as AccountOwnerType,
    );

    this.showPod = config.showPod && this.isBeneficiariesEnabled;
    this.showSubAccountType = config.labels.showSubAccountType;

    if (this.showSubAccountType) {
      this.subAccountTypeValues = this.generateDropdownMenuData(config.subAccountTypes);
    }

    this.localAccountDetailsContent = config.localContent;

    this.updateLabels();
  }

  // TODO: Refactor to reduce cyclomatic complexity (currently 22, max 10)
  // eslint-disable-next-line complexity
  onSubmit(): boolean {
    // For testing error states; also checks whether all the input fields are populated or not
    // Returns true to tell the parent component to disallow the continue flow
    // Returns false to tell the parent component to allow the continue flow

    let hasOwnerError = false;
    let hasError = false;

    if (this.ownerContainer) {
      if (!this.ownerContainer.hasErrorStates()) {
        this.ownerContainer.handleSubmit(this.accountType, this.isEditMode);
      } else {
        WebLogger.log({
          message: "AccountDetails failed to get owner data for submission",
          level: "error",
        });
        hasOwnerError = true;
      }

      if (this.fromVgTable) {
        return hasOwnerError;
      }
    }

    // Institution typeahead validation for eligible account types — fail closed if ViewChild hasn't resolved
    if (this.isInstitutionEligible()) {
      if (!this.institutionTypeahead || this.institutionTypeahead.hasError()) {
        hasError = true;
      }
    }

    //Account level validation
    const hasAccountError = this.hasErrorStates();

    let hasBeneficiaryError = false;
    if (this.payableOnDeathDesignation && this.beneficiarySection?.hasErrors()) {
      hasBeneficiaryError = true;
      WebLogger.log({
        message: "Account submission blocked due to beneficiary validation errors",
        level: "warn",
        payload: {
          event_type: "account_submit_beneficiary_validation_error",
          account_type: this.accountType,
          beneficiaries_toggle_enabled: this.isBeneficiariesEnabled,
        },
      });
    }

    if (hasError || hasOwnerError || hasAccountError || hasBeneficiaryError) {
      return true;
    }

    // Handle other account types (existing logic)
    // This iteration removes the $ and commas
    const sanitizedBalance = this.formValidationService.sanitizeCurrencyInput(this.balance);
    const sanitizedMortgageAmount = this.mortgageAmount
      ? this.formValidationService.sanitizeCurrencyInput(this.mortgageAmount)
      : null;

    let submitMappedAccountType;

    if (this.globalStore.getGuidedFlowState(this.accountType).isEdit) {
      // Need to check specifically for any accounts that don't have a subAccountType, Bank in this case
      if (this.accountType === "bank-accounts") {
        submitMappedAccountType = "UNKNOWN";
      } else {
        // Specifically for the EDIT flow: If the "Account Type" dropdown on the UI gets selected, it updates its value to the value we expect from the create flow (i.e. fixed-annuity) inside the subAccountDropdownChangeHandler function
        // If that updated value exists in the mappedAccountTypesMap we're trying to send to AGO/MEZ, set submitMappedAccountType to that
        // If not, set it to the current subAccountType value
        submitMappedAccountType =
          mappedAccountTypesMap[this.accountType][this.subAccountType] || this.subAccountType;
      }
    } else {
      // Specifically handling the case where the accountType is Bank
      if (this.accountType === "bank-accounts") {
        submitMappedAccountType = "UNKNOWN";
        // Else if the accountType and its related subAccountType exist in the submitMappedAccountTypesMap, set the submitMappedAccountType to the mapped value
      } else if (
        mappedAccountTypesMap[this.accountType] &&
        mappedAccountTypesMap[this.accountType][this.subAccountType]
      ) {
        submitMappedAccountType = mappedAccountTypesMap[this.accountType][this.subAccountType];
      }
    }

    if (this.submitAccountOwner !== "JOINT_OWNER") {
      this.jointType = null;
    }

    const data: Record<string, any> = {
      accountName: this.accountName,
      balance: Math.floor(parseFloat(sanitizedBalance) || 0),
      accountType: submitMappedAccountType,
      subAccountType: this.subAccountType,
      accountOwner: this.submitAccountOwner,
      jointType: this.jointType,
      accountOwnerUuid: this.accountOwnerUuid,
      mortgageAmount: sanitizedMortgageAmount ? parseFloat(sanitizedMortgageAmount) : null,
      payableOnDeathDesignation: this.payableOnDeathDesignation,
    };

    if (this.isInstitutionEligible() && this.financialInstitutionId) {
      data["financialInstitutionId"] = this.financialInstitutionId;
      if (this.financialInstitutionName) {
        data["financialInstitutionName"] = this.financialInstitutionName;
      }
    }

    // Include beneficiary data when POD is checked
    if (this.payableOnDeathDesignation && this.beneficiarySection) {
      data.beneficiaryState = this.beneficiarySection.beneficiaryState;
      WebLogger.log({
        message: "AccountDetails including beneficiaryState in submit data",
        level: "info",
        payload: {
          event_type: "account_details_submit_bene_state",
          primary_count: data.beneficiaryState?.primaryBeneficiaries?.length || 0,
          backup_count: data.beneficiaryState?.backupBeneficiaries?.length || 0,
        },
      });
    }

    // Updates the tempState with the related data
    this.globalStore.updateGuidedFlowTempState(this.accountType, data);
    // Clear the tempState after saving
    this.globalStore.mergeGuidedFlowTempState(this.accountType);

    return false;
  }

  // Returns false if there is an error
  hasErrorStates(): boolean {
    // For trust accounts, validation is handled by the AccountOwnerContainer
    // We only need to check basic validation for non-trust account types
    //TODO: Change this to ownerType. This is reponsible for checking the ownerContainer validation state
    if (this.accountType === AccountType.TRUST || this.ownerType === "trust") {
      // Trust validation is delegated to the AccountOwnerContainer
      // validOwnerInput is updated via onOwnerValidationChange callback
      return !this.validOwnerInput;
    }

    // Institution-eligible types don't use the accountName field — skip its validation
    const nameToValidate = this.isInstitutionEligible() ? "skip" : this.accountName;

    const validation = this.formValidationService.validateAllInputs(
      nameToValidate,
      this.balance,
      this.subAccountType,
      this.mortgageAmount,
      this.accountType,
      this.showSubAccountType,
    );

    // Update component validation state
    this.validAccountNameInput = validation.validAccountNameInput;
    this.validBalanceInput = validation.validBalanceInput;
    this.validSubAccountTypeInput = validation.validSubAccountTypeInput;
    this.validMortgageInput = validation.validMortgageInput;

    return this.formValidationService.hasValidationErrors(validation, this.accountType);
  }

  generateDropdownMenuData(items: DropdownMenuItem[]): DropdownMenuData {
    return {
      groups: [
        {
          items: items,
        },
      ],
    };
  }

  isAsset(item: Account | Asset): item is Asset {
    return "assetName" in item;
  }

  onBeneficiaryStateChange(state: BeneficiaryState): void {
    this.savedBeneficiaryState = state;
    this.globalStore.updateGuidedFlowTempState(this.accountType, {
      beneficiaryState: state,
      payableOnDeathDesignation: this.payableOnDeathDesignation,
    });
  }

  onBeneficiaryValidationChange(isValid: boolean): void {
    this.validBeneficiaryInput = isValid;
  }

  handleHelpClick(): void {
    if (this.onHelpClick) {
      this.onHelpClick();
    }
  }

  /**
   * Handler for owner data changes from AccountOwnerContainer component
   */
  onOwnerDataChange(ownerData: OwnerData | null): void {
    this.ownerData = ownerData;

    if (ownerData) {
      // Update the existing properties based on owner data
      this.accountOwner = ownerData.ownerId;
      this.accountOwnerUuid = ownerData.ownerUuid || "";
      this.ownerType =
        this.accountTypeConfigService.getAccountOwnerTypeMap()[ownerData.ownerType] ||
        ownerData.ownerType;
      this.jointType = ownerData.jointType || null;

      // For trust accounts, handle additional trust-specific data
      if (ownerData.ownerType === AccountOwnerType.TRUST) {
        // REMOVED: DO NOT overwrite subAccountType with trust type!
        // Trust type is metadata about the trust owner, NOT about the account type
        // The account's subAccountType should remain unchanged (e.g., "savings", "checking", etc.)
      }
    }
  }

  /**
   * Handler for validation state changes from AccountOwnerContainer component
   */
  onOwnerValidationChange(isValid: boolean): void {
    this.validOwnerInput = isValid;
  }
}
